/**
 * PIIDataQualityDashboard - Shows PII coverage metrics and data quality
 * Helps users understand how well their PII detection is working
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Statistic, Progress, Table, Tag, Select, DatePicker, Spin, Alert, Empty } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  LineChartOutlined,
  DatabaseOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { getPIIDataQuality } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';
import { TGDataTable } from '../../../components/common';
import { colors, spacing } from '../../../theme/designTokens';
import LastScanFilterBar from './LastScanFilterBar';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const { RangePicker } = DatePicker;
const { Option } = Select;

function PIIDataQualityDashboard({ projectId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [timeRange, setTimeRange] = useState('7d'); // 7d, 30d, 90d
  const [provider, setProvider] = useState(null);
  const [dateFilter, setDateFilter] = useState(null);

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId, timeRange, provider]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getPIIDataQuality(projectId, timeRange, provider);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch PII data quality:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  // Generate dynamic filter options for Recent Events (BEFORE early returns to follow Rules of Hooks)
  const providerOptions = useMemo(() => {
    if (!data?.recent_events) return [];
    const providers = [...new Set(data.recent_events.map(e => e.provider))].filter(Boolean).sort();
    return providers.map(p => ({ label: p.charAt(0).toUpperCase() + p.slice(1), value: p }));
  }, [data?.recent_events]);

  const piiDetectedOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  const sourceOptions = useMemo(() => {
    if (!data?.recent_events) return [];
    const sources = [...new Set(data.recent_events.map(e => e.detection_source || 'scanner'))].filter(Boolean).sort();
    return sources.map(s => ({ label: s.charAt(0).toUpperCase() + s.slice(1), value: s }));
  }, [data?.recent_events]);

  const piiTypesOptions = useMemo(() => {
    if (!data?.recent_events) return [];
    const types = new Set();
    data.recent_events.forEach(e => {
      if (e.pii_types && Array.isArray(e.pii_types)) {
        e.pii_types.forEach(t => types.add(t));
      }
    });
    return Array.from(types).sort().map(t => ({ label: t, value: t }));
  }, [data?.recent_events]);

  // Filter recent events by date
  const filteredRecentEvents = useMemo(() => {
    if (!data?.recent_events) return [];
    if (!dateFilter || !dateFilter.date) return data.recent_events;

    const { operator, date } = dateFilter;
    const filterTime = new Date(date).setHours(0, 0, 0, 0);

    return data.recent_events.filter((event) => {
      if (!event.ts) return false;
      const eventTime = new Date(event.ts).setHours(0, 0, 0, 0);

      switch (operator) {
        case 'on':
          return eventTime === filterTime;
        case 'after':
          return eventTime > filterTime;
        case 'before':
          return eventTime < filterTime;
        default:
          return true;
      }
    });
  }, [data?.recent_events, dateFilter]);

  if (loading && !data) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <Empty description="No data available" />
      </Card>
    );
  }

  // Calculate coverage status
  const coverageScore = data.coverage_percentage || 0;
  const coverageStatus = coverageScore >= 90 ? 'success' : coverageScore >= 70 ? 'warning' : 'exception';
  const coverageIcon = coverageScore >= 90 ? <CheckCircleOutlined /> : <WarningOutlined />;

  // Prepare chart data
  const chartData = {
    labels: data.trend?.dates || [],
    datasets: [
      {
        label: 'PII Coverage %',
        data: data.trend?.coverage || [],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Events with PII',
        data: data.trend?.events_with_pii || [],
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y1'
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'PII Detection Coverage Trend'
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Coverage %'
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Events Count'
        },
        grid: {
          drawOnChartArea: false,
        }
      }
    }
  };

  // Provider breakdown table
  const providerColumns = [
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      render: (provider) => <Tag color="blue">{provider}</Tag>
    },
    {
      title: 'Total Events',
      dataIndex: 'total_events',
      key: 'total_events',
      align: 'right'
    },
    {
      title: 'Events with PII',
      dataIndex: 'events_with_pii',
      key: 'events_with_pii',
      align: 'right'
    },
    {
      title: 'Coverage',
      dataIndex: 'coverage',
      key: 'coverage',
      align: 'right',
      render: (coverage) => (
        <span style={{ color: coverage >= 90 ? '#52c41a' : coverage >= 70 ? '#faad14' : '#f5222d' }}>
          {coverage.toFixed(1)}%
        </span>
      )
    }
  ];

  // Recent events table
  const recentEventsColumns = [
    {
      title: 'Event ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: 100,
      render: (provider) => <Tag color={colors.primary[500]}>{provider}</Tag>,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: providerOptions,
    },
    {
      title: 'PII Detected',
      dataIndex: 'pii_detected',
      key: 'pii_detected',
      width: 120,
      render: (detected) => detected ? <Tag color={colors.severity.critical.main}>Yes</Tag> : <Tag>No</Tag>,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: piiDetectedOptions,
    },
    {
      title: 'PII Types',
      dataIndex: 'pii_types',
      key: 'pii_types',
      render: (types) => (
        <div style={{ display: 'flex', gap: spacing.xs, flexWrap: 'wrap' }}>
          {types && types.length > 0 ? types.map(type => (
            <Tag key={type} color="orange" style={{ margin: 0 }}>{type}</Tag>
          )) : '-'}
        </div>
      ),
      filterable: true,
      filterType: 'multiselect',
      filterOptions: piiTypesOptions,
    },
    {
      title: 'Source',
      dataIndex: 'detection_source',
      key: 'detection_source',
      width: 120,
      render: (source) => (
        <Tag color={source === 'instrumented' ? colors.status.pass : colors.primary[500]}>
          {source || 'scanner'}
        </Tag>
      ),
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: sourceOptions,
    },
    {
      title: 'Timestamp',
      dataIndex: 'ts',
      key: 'ts',
      width: 150,
      render: (ts) => new Date(ts).toLocaleString(),
      sortable: true,
    }
  ];

  return (
    <div>
      {/* Filters */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={16} align="middle">
          <Col>
            <strong>Time Range:</strong>
          </Col>
          <Col>
            <Select value={timeRange} onChange={setTimeRange} style={{ width: 120 }}>
              <Option value="7d">Last 7 days</Option>
              <Option value="30d">Last 30 days</Option>
              <Option value="90d">Last 90 days</Option>
            </Select>
          </Col>
          <Col>
            <strong>Provider:</strong>
          </Col>
          <Col>
            <Select
              value={provider}
              onChange={setProvider}
              allowClear
              placeholder="All providers"
              style={{ width: 150 }}
            >
              <Option value="openai">OpenAI</Option>
              <Option value="anthropic">Anthropic</Option>
              <Option value="bedrock">AWS Bedrock</Option>
              <Option value="github">GitHub</Option>
              <Option value="slack">Slack</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Key Metrics */}
      <Row gutter={16} style={{ marginBottom: '16px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="PII Coverage"
              value={coverageScore}
              precision={1}
              suffix="%"
              prefix={coverageIcon}
              valueStyle={{ color: coverageScore >= 90 ? '#3f8600' : coverageScore >= 70 ? '#faad14' : '#cf1322' }}
            />
            <Progress
              percent={coverageScore}
              status={coverageStatus}
              showInfo={false}
              style={{ marginTop: '8px' }}
            />
            <div style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
              {data.events_with_flag} / {data.total_events} events have PII flag
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Events"
              value={data.total_events}
              prefix={<DatabaseOutlined />}
            />
            <div style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
              {data.events_with_pii} events contain PII
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Scanner Coverage"
              value={data.scanner_coverage || 0}
              precision={1}
              suffix="%"
              prefix={<LineChartOutlined />}
            />
            <div style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
              {data.scanner_events || 0} events scanned
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Instrumented"
              value={data.instrumented_coverage || 0}
              precision={1}
              suffix="%"
              prefix={<CheckCircleOutlined />}
            />
            <div style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
              {data.instrumented_events || 0} events with flags
            </div>
          </Card>
        </Col>
      </Row>

      {/* Alert for low coverage */}
      {coverageScore < 90 && (
        <Alert
          type={coverageScore < 70 ? 'error' : 'warning'}
          message={`PII Coverage Below Target (${coverageScore.toFixed(1)}%)`}
          description={
            <div>
              <p>Your PII detection coverage is below the recommended 90% threshold. This means some events may not have PII detection applied.</p>
              <strong>Recommendations:</strong>
              <ul style={{ marginTop: '8px', marginBottom: 0 }}>
                {data.scanner_coverage < 50 && <li>Enable PII scanner for automatic detection</li>}
                {data.instrumented_coverage < 50 && <li>Add PII detection flags to your instrumentation code</li>}
                <li>Run a batch re-scan on historical events</li>
              </ul>
            </div>
          }
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      {/* Trend Chart */}
      <Card title="Coverage Trend" style={{ marginBottom: '16px' }}>
        {data.trend && data.trend.dates && data.trend.dates.length > 0 ? (
          <Line data={chartData} options={chartOptions} />
        ) : (
          <Empty description="No trend data available" style={{ padding: '40px' }} />
        )}
      </Card>

      {/* Provider Breakdown */}
      <Card title="Coverage by Provider" style={{ marginBottom: '16px' }}>
        {data.by_provider && data.by_provider.length > 0 ? (
          <Table
            dataSource={data.by_provider}
            columns={providerColumns}
            rowKey="provider"
            pagination={false}
            size="small"
          />
        ) : (
          <Empty description="No provider data available" />
        )}
      </Card>

      {/* Recent Events */}
      <Card title="Recent Events" extra={<ClockCircleOutlined />}>
        {data.recent_events && data.recent_events.length > 0 ? (
          <TGDataTable
            data={filteredRecentEvents}
            columns={recentEventsColumns}
            rowKey="id"
            loading={false}
            pagination="client"
            pageSize={10}
            showExport
            showColumnConfig
            showFilters
            exportFilename="pii-recent-events"
            presetKey="trustgrid.pii-data-quality.recent-events"
            toolbar={
              <LastScanFilterBar
                dateFilter={dateFilter}
                onDateFilterChange={setDateFilter}
              />
            }
            externalFilterCount={dateFilter ? 1 : 0}
            onClearExternalFilters={() => setDateFilter(null)}
          />
        ) : (
          <Empty description="No recent events available" />
        )}
      </Card>
    </div>
  );
}

export default PIIDataQualityDashboard;
